/**
 * File: CaseManagement.jsx
 * Path: /src/pages/CaseManagement.jsx
 * Description: Dedicated page showcasing Veritect.ai's Case Management AI solution
 * Key Features: Interactive case management visualization, feature breakdown, and benefits section
 * test update
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import CaseManagementDashboard from '../components/solutions/CaseManagementDashboard';
import CaseWorkflowDashboard from '../components/solutions/CaseWorkflowDashboard';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ModernStatsSection from '../components/common/ModernStatsSection';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const CaseManagement = () => {
  // Initialize observer for scroll animations
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Features data
  const features = [
    {
      title: "Intelligent Case Organization",
      description: "Automatically organize case documents, deadlines, contacts, and communications in a central, searchable repository with AI-powered categorization.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      )
    },
    {
      title: "Automated Deadline Management",
      description: "AI-powered system automatically calculates, tracks, and alerts you about critical case deadlines based on jurisdiction rules and court schedules.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "Efficient Workflow Management",
      description: "Streamline your firm's workflows with customizable templates, task assignments, approvals, and progress tracking for each case type.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    },
    {
      title: "Team Collaboration Tools",
      description: "Enhance team productivity with integrated messaging, document sharing, version control, and real-time collaboration features.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: "Client Portal Access",
      description: "Provide clients with secure, 24/7 access to case information, document uploads, billing details, and communication through a branded client portal.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      )
    },
    {
      title: "Comprehensive Analytics",
      description: "Gain valuable insights into firm performance, case outcomes, resource allocation, and profitability with advanced reporting and analytics tools.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  // How it works steps
  const workflowSteps = [
    {
      number: "01",
      title: "Case Intake",
      description: "Quickly capture essential case details, client information, and documents with AI-assisted data extraction and organization."
    },
    {
      number: "02",
      title: "Workflow Automation",
      description: "Apply customizable, practice-specific workflows that automate routine tasks and guide your team through case processes."
    },
    {
      number: "03",
      title: "Active Case Management",
      description: "Track deadlines, assign tasks, collaborate on documents, and monitor case progress in a centralized dashboard."
    },
    {
      number: "04",
      title: "Insights & Reporting",
      description: "Analyze case performance, team productivity, and practice metrics to optimize your firm's operations and profitability."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-blue-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-primary-100 rounded-tr-full opacity-70"></div>
        
        {/* Floating decorative elements */}
        <motion.div 
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{ 
            y: [0, -15, 0], 
            opacity: [0.4, 0.7, 0.4] 
          }}
          transition={{ 
            duration: 5, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
        >
          <div className="w-16 h-16 border-2 border-blue-500/30 rounded-lg rotate-12"></div>
        </motion.div>
        
        <motion.div 
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{ 
            y: [0, 15, 0], 
            opacity: [0.3, 0.6, 0.3] 
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity, 
            repeatType: "reverse", 
            delay: 1 
          }}
        >
          <div className="w-12 h-12 border-2 border-primary-500/30 rounded-full"></div>
        </motion.div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  AI-Powered <span className="gradient-text">Case Management</span> Platform
                </h1>
                <p className="text-xl text-dark-600 mb-8">
                  Streamline your legal practice with our intelligent case management system. Automate workflows, manage deadlines, and enhance collaboration while gaining valuable insights.
                </p>
                
                <div className="flex flex-wrap gap-4 mb-10">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/contact"
                    className="shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">Request a Demo</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    to="/solutions/case-management#how-it-works"
                  >
                    See How It Works
                  </Button>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex -space-x-2">
                    {['#3b82f6', '#2563eb', '#1d4ed8', '#1e40af'].map((color, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: color, zIndex: 4-i }}>
                        {['H', 'V', 'L', 'S'][i]}
                      </div>
                    ))}
                  </div>
                  <div className="text-dark-500">
                    <strong className="text-dark-700">1,000+</strong> law firms trust our platform
                  </div>
                </div>
              </motion.div>
              
              {/* Right Side - Interactive Visualization */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {/* Case Management Dashboard Component */}
                <CaseManagementDashboard />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <ModernStatsSection 
        title="Transform Your Practice Management"
        description="See the real impact our AI-powered case management platform is making for law firms."
        stats={[
          { number: '75%', label: 'Reduction in Administrative Tasks' },
          { number: '45%', label: 'Increase in Case Throughput' },
          { number: '98%', label: 'Deadline Compliance Rate' },
          { number: '3.2x', label: 'Return on Investment' }
        ]} 
      />
      
      {/* Features Section */}
      <section className="py-20 bg-white" ref={sectionRef}>
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Key Features</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Powerful Case Management Capabilities
            </h2>
            <p className="text-dark-500 text-lg">
              Our case management platform combines cutting-edge AI with deep legal expertise 
              to deliver a practice management experience that's smarter, more efficient, and more insightful.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full flex flex-col items-start">
                  <div className="rounded-lg p-3 bg-blue-50 text-blue-600 mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-dark-500">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl"></div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Case Management, Reimagined
            </h2>
            <p className="text-dark-500 text-lg">
              Our intuitive workflow makes case management more efficient and effective than ever before.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              >
                {/* Connector line between steps (hidden on mobile) */}
                {index < workflowSteps.length - 1 && (
                  <div className="absolute hidden lg:block top-6 left-full w-full h-0.5 bg-blue-200 z-0">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45">
                      <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 256 512">
                        <path d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z"></path>
                      </svg>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-600 text-white text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-dark-500">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-primary-600 font-semibold">Benefits</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Why Choose Veritect Case Management AI
              </h2>
              <p className="text-dark-600 mb-8">
                Our AI-powered case management platform doesn't just organize cases—it transforms how legal professionals
                approach their practice, enabling greater efficiency, better outcomes, and improved client service.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Streamlined Operations",
                    description: "Eliminate administrative bottlenecks and standardize case handling with automated workflows tailored to your practice areas."
                  },
                  {
                    title: "Enhanced Collaboration",
                    description: "Improve teamwork with real-time document collaboration, task management, and centralized communication for each case."
                  },
                  {
                    title: "Risk Mitigation",
                    description: "Reduce errors and avoid missed deadlines with automated calendaring, conflict checking, and compliance monitoring."
                  },
                  {
                    title: "Data-Driven Insights",
                    description: "Make informed decisions with comprehensive analytics on case progress, team performance, and practice profitability."
                  }
                ].map((benefit, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <div className="mr-4 text-blue-600 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">{benefit.title}</h4>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10">
                <Button 
                  variant="primary"
                  to="/contact"
                  className="shadow-lg"
                >
                  Schedule a Demo
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Case Workflow Dashboard Component */}
              <CaseWorkflowDashboard />
              
              {/* Floating elements for visual interest */}
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-blue-100 rounded-lg rotate-12 flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonial Section */}
      <section className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-400 via-blue-400 to-primary-400"></div>
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" />
            </g>
          </svg>
        </div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Hear From Our Clients
            </h2>
            <p className="text-dark-500 text-lg">
              Leading law firms are transforming their practice management with Veritect.ai
            </p>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8 relative max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Quote mark */}
            <div className="absolute top-6 left-8 text-blue-300 opacity-30 text-6xl font-serif">
              "
            </div>
            
            <div className="pl-8">
              <blockquote className="text-lg mb-6 text-dark-700">
                Veritect's case management platform has revolutionized how our firm operates. The automated deadline 
                management alone has eliminated missed deadlines completely, while the workflow automation has reduced 
                our administrative workload by at least 70%. Our attorneys now have more time for high-value client work, 
                and the analytics have helped us identify and resolve bottlenecks in our processes.
              </blockquote>
              
              <div className="flex items-center">
                <div className="mr-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-primary-500 flex items-center justify-center text-white text-lg font-bold">
                    ET
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800">Emily Thompson</h4>
                  <p className="text-dark-500 text-sm">Managing Partner, Thompson Legal</p>
                </div>
                <div className="ml-auto flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* FAQs Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-dark-500 text-lg">
              Everything you need to know about our Case Management AI platform
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How quickly can we implement Veritect's case management system?",
                answer: "Most firms are up and running with our platform within 2-4 weeks. Our implementation process includes data migration, custom workflow configuration, team training, and integration with your existing tools. We assign a dedicated implementation specialist to ensure a smooth transition with minimal disruption to your practice."
              },
              {
                question: "Can we customize workflows for different practice areas?",
                answer: "Yes, our platform offers extensive customization options for different practice areas. You can create specialized workflows for litigation, corporate, family law, estate planning, and more. Each workflow can have its own task templates, document automation, deadline rules, and team assignments tailored to that practice area's specific needs."
              },
              {
                question: "How does the AI-powered deadline management work?",
                answer: "Our AI deadline management system automatically calculates critical dates based on court rules, statutes of limitations, and procedural requirements across different jurisdictions. It accounts for court holidays, weekends, and local rule variations. The system creates cascading deadlines, sends smart notifications, and automatically adjusts all related dates when a triggering event changes."
              },
              {
                question: "What security measures are in place to protect client data?",
                answer: "We implement bank-level security with 256-bit encryption for all data, both in transit and at rest. Our platform is hosted in SOC 2 Type II certified data centers with regular penetration testing and vulnerability assessments. We offer role-based access controls, two-factor authentication, audit logs, and compliance with legal industry standards including HIPAA and GDPR where applicable."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-dark-600">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <ModernCtaSection
        title="Ready to Transform Your Practice Management?"
        description="Join innovative law firms using Veritect.ai to streamline operations, enhance collaboration, and deliver better client service."
        buttonText="Schedule a Demo"
        buttonLink="/contact"
      />
    </motion.div>
  );
};

export default CaseManagement;